// src/utils/invoiceUtils.js
/**
 * Invoice utility functions for generating business-appropriate references
 */

/**
 * Get organization name from localStorage authUser
 * @returns {string} Organization name or fallback
 */
export const getOrganizationName = () => {
    try {
        const authUserData = localStorage.getItem('authUser');
        if (authUserData) {
            const authUser = JSON.parse(authUserData);
            // Try different possible property names for organization
            const orgName = authUser.organizationName || 
                           authUser.organization_name || 
                           authUser.companyName || 
                           authUser.company_name ||
                           authUser.businessName ||
                           authUser.business_name ||
                           authUser.clientName ||
                           authUser.client_name ||
                           'EMPRESA'; // Fallback
            
            // Clean organization name (remove spaces, special chars, convert to uppercase)
            return orgName.toString()
                         .toUpperCase()
                         .replace(/[^A-Z0-9]/g, '')
                         .substring(0, 20); // Limit length
        }
    } catch (error) {
        console.error('Error reading organization name from authUser:', error);
    }
    
    return 'EMPRESA'; // Default fallback
};

/**
 * Generate timestamp in dd-mm-yy-hh-mm-ss format
 * @returns {string} Formatted timestamp
 */
export const generateTimestamp = () => {
    const now = new Date();
    
    const day = String(now.getDate()).padStart(2, '0');
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const year = String(now.getFullYear()).slice(-2); // Last 2 digits
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');
    
    return `${day}-${month}-${year}-${hours}-${minutes}-${seconds}`;
};

/**
 * Generate external reference using organization name and timestamp
 * Format: {organizationName}-{dd-mm-yy-hh-mm-ss}
 * @returns {string} External reference
 */
export const generateExternalReference = () => {
    const organizationName = getOrganizationName();
    const timestamp = generateTimestamp();
    
    return `${organizationName}-${timestamp}`;
};

/**
 * Legacy functions kept for backward compatibility
 */

// Option 1: Simple sequential with date
export const generateSimpleReference = async () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    
    // Get next sequence number (this would come from backend)
    const sequence = Math.floor(Math.random() * 9999) + 1; // Mock for now
    
    return `FAC-${year}${month}${day}-${String(sequence).padStart(4, '0')}`;
    // Example: FAC-20251024-0001
};

// Option 2: Store/User based reference
export const generateStoreReference = async (storeCode = 'FER', userId = null) => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    
    // Get next sequence number for this month
    const sequence = Math.floor(Math.random() * 999) + 1; // Mock for now
    
    const userSuffix = userId ? `-U${userId}` : '';
    
    return `${storeCode}-${year}${month}-${String(sequence).padStart(3, '0')}${userSuffix}`;
    // Examples: 
    // FER-202510-001 (Ferretería)
    // FER-202510-001-U123 (with user ID)
};

// Option 3: Product category based
export const generateCategoryReference = async (mainCategory = 'GEN') => {
    const today = new Date();
    const year = String(today.getFullYear()).slice(-2); // Last 2 digits
    const dayOfYear = Math.floor((today - new Date(today.getFullYear(), 0, 0)) / 86400000);
    
    const sequence = Math.floor(Math.random() * 99) + 1; // Mock for now
    
    return `${mainCategory}${year}${String(dayOfYear).padStart(3, '0')}${String(sequence).padStart(2, '0')}`;
    // Examples:
    // HER25297001 (Herramientas - Tools)
    // MAT25297002 (Materiales - Materials) 
    // ELE25297003 (Eléctricos - Electrical)
    // PLO25297004 (Plomería - Plumbing)
};

// Option 4: Customer type based
export const generateCustomerReference = async (customerType = 'CF') => {
    const today = new Date();
    const timestamp = today.getTime().toString().slice(-8); // Last 8 digits of timestamp
    
    return `${customerType}-${timestamp}`;
    // Examples:
    // CF-12345678 (Consumidor Final)
    // EMP-12345679 (Empresa)
    // GOV-12345680 (Gobierno)
};

// Option 5: Most business-friendly for hardware store
export const generateHardwareStoreReference = async (cartData = {}) => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    
    // Analyze cart to determine main category
    let categoryCode = 'GEN'; // General
    
    if (cartData.cartItems && cartData.cartItems.length > 0) {
        const categories = cartData.cartItems.map(item => item.CategoryName?.toUpperCase() || 'GENERAL');
        const mostCommon = categories.reduce((a, b, _, arr) => 
            arr.filter(v => v === a).length >= arr.filter(v => v === b).length ? a : b
        );
        
        // Map categories to codes
        const categoryMap = {
            'HERRAMIENTAS': 'HER',
            'TOOLS': 'HER',
            'MATERIALES': 'MAT', 
            'MATERIALS': 'MAT',
            'ELECTRICOS': 'ELE',
            'ELECTRICAL': 'ELE',
            'PLOMERIA': 'PLO',
            'PLUMBING': 'PLO',
            'PINTURAS': 'PIN',
            'PAINTS': 'PIN',
            'FERRETERIA': 'FER',
            'HARDWARE': 'FER',
            'CONSTRUCCION': 'CON',
            'CONSTRUCTION': 'CON'
        };
        
        categoryCode = categoryMap[mostCommon] || 'GEN';
    }
    
    // Generate sequence (would come from backend)
    const sequence = Math.floor(Math.random() * 999) + 1;
    
    return `${categoryCode}${year}${month}${day}${String(sequence).padStart(3, '0')}`;
    // Examples:
    // HER20241024001 (Herramientas)
    // MAT20241024002 (Materiales)
    // ELE20241024003 (Eléctricos)
    // PLO20241024004 (Plomería)
    // GEN20241024005 (General)
};

/**
 * Configuration for reference generation
 */
export const INVOICE_REFERENCE_CONFIG = {
    // Choose your preferred format:
    format: 'organization_timestamp', // NEW: 'organization_timestamp', 'simple', 'store', 'category', 'customer', 'hardware_store'
    
    // Store settings
    storeCode: 'FER', // FER = Ferretería
    storeName: 'Ferretería HorizontePyME',
    
    // Category codes for hardware store
    categoryMap: {
        'HERRAMIENTAS': 'HER',
        'MATERIALES': 'MAT', 
        'ELECTRICOS': 'ELE',
        'PLOMERIA': 'PLO',
        'PINTURAS': 'PIN',
        'FERRETERIA': 'FER',
        'CONSTRUCCION': 'CON',
        'GENERAL': 'GEN'
    }
};

/**
 * Main function to generate invoice reference based on configuration
 */
export const generateInvoiceReference = async (cartData = {}) => {
    const config = INVOICE_REFERENCE_CONFIG;
    
    switch (config.format) {
        case 'organization_timestamp':
            return generateExternalReference();
        case 'simple':
            return await generateSimpleReference();
        case 'store':
            return await generateStoreReference(config.storeCode);
        case 'category':
            return await generateCategoryReference();
        case 'customer':
            return await generateCustomerReference();
        case 'hardware_store':
            return await generateHardwareStoreReference(cartData);
        default:
            return generateExternalReference(); // Default to new format
    }
};

export default {
    generateInvoiceReference,
    generateExternalReference,
    getOrganizationName,
    generateTimestamp,
    generateSimpleReference,
    generateStoreReference,
    generateCategoryReference,
    generateCustomerReference,
    generateHardwareStoreReference,
    INVOICE_REFERENCE_CONFIG
};